import { useState } from "react";
import type {
  CheckBoxEventValue,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import {
  Alert,
  Box,
  CheckBox,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import { useGetFeatureAccess } from "@dealer-center/ddc-pages-configuration-fetch";
import type { OperatorAuthority } from "@dealer-center/ddc-pages-configuration-interface";
import type { UserInfoSubject } from "../UserInfoSubject";
import { ReadOnlyNotice } from "../ReadOnlyNotice";
import { SCREEN_READER_ONLY } from "../StatusRegion/screenReaderOnly";
import { TabState } from "../TabState";
import { useUserInfoArea } from "../userInfoAreaContext";
import { PermissionGroup } from "./PermissionGroup";
import { groupPermissions } from "./groupPermissions";

export interface FeatureAccessTabProps {
  subject: UserInfoSubject;
  /**
   * The **operator's** authority. **Absent means read-only**, and that default carries real weight:
   *
   * - **My Info passes nothing at all**, so it stays read-only with no change to its code — now
   *   enforced by the *absence of a value* rather than by a hardcoded `disabled`.
   * - **Enabling is opt-in and requires the operator's own record**, so there is **no path** by
   *   which a row becomes editable from data about the **subject**. That is FR-054 enforced by
   *   construction rather than by discipline.
   *
   * ⚠️ **This is NOT a `FeatureAccessResponse`, and the distinction is SC-016's structural guard.**
   * The subject's response and the operator's are the same shape from the same source, differing
   * only in whose they are (FR-058a) — the pair a reviewer cannot tell apart. Because this prop
   * accepts only `OperatorAuthority`, passing the subject's response here is a **`typecheck`
   * failure**, which is what SC-016 means by "impossible without an error at build time". A runtime
   * check would only fire on a path someone already took.
   */
  authority?: OperatorAuthority;
}

/**
 * Every feature the account is entitled to, grouped, each marked granted or not granted.
 *
 * **One query, and it is not the profile query.** `useGetFeatureAccess` supplies both the rows and
 * the `administrator` flag, because CC9 moved that flag onto this response (interface
 * `FeatureAccessResponse` is the single normative statement of its location). Reading it from
 * `useGetUserInfo` is a compile error by design (spec SC-011) — and would silently report every user
 * as not an administrator if the types allowed it. This tab therefore has **no dependency on US1**.
 *
 * `administrator` and the rows are display data, never authorization (Monorepo Principle VII). Real
 * authorization is `StrictlyInternalGuard`, the LD flag, and the lambda's `isInternalUser`.
 *
 * **Nothing here saves** (FR-083 — no write endpoint exists for feature access), but as of
 * 2026-09-17 the permission rows are no longer *blanket*-disabled: `authority` now gates the row's
 * `disabled` as well as what it says, so FR-046's real rule is visible and exercisable instead of
 * masked. See the `isRowDisabled` note in `PermissionGroup` for the exact condition — read-only with
 * no `authority`, per-row grantability with one. SC-004 stays absolute either way: a permission the
 * operator does not hold is disabled **and** carries its reason, conveyed before any save rather
 * than as a rejected one (Edge Cases).
 *
 * The administrator entry follows the same model, gated on the operator's own administrator flag.
 */
export const FeatureAccessTab = ({
  subject,
  authority
}: FeatureAccessTabProps) => {
  const { data, isPending, isError } = useGetFeatureAccess({
    userId: subject.userId,
    accountId: subject.accountId,
    // `getCurrent()`, not `getLocale()` — see the note in `LoginHistoryTab`.
    locale: Locale.getCurrent()
  });

  // Telemetry attribution only — see `userInfoAreaContext`.
  const area = useUserInfoArea();

  // Local, unsaved toggle; the shell's `key={subject.userId}` resets it per user.
  const [isAdministratorOverride, setIsAdministratorOverride] = useState<
    boolean | undefined
  >();
  const isAdministratorNotGrantable = Boolean(
    authority && !authority.isAccountAdministrator
  );
  const isAdministratorChecked = Boolean(
    data && (isAdministratorOverride ?? data.administrator)
  );
  const handleAdministratorToggle = (
    event: InterstateOnChangeEvent<CheckBoxEventValue>
  ) => setIsAdministratorOverride(Boolean(event.target?.value?.checked));

  const permissions = data?.permissions ?? [];
  const groups = groupPermissions(permissions, {
    departmentTemplate: Locale.getText("CUSTOM_WEBSITE_DEPARTMENT") as string,
    /*
     * How a CC9 resource code points at its label key. Needed only where Label Services has no
     * `permission.resource.<name>` entry, because new keys are registered in SCREAMING_SNAKE
     * form rather than in that closed dotted namespace. Adding another resource is one line
     * here plus one key in `labels.json` — the codes are `permission.name`, not label keys.
     */
    resources: {
      "Inventory.FeeManager": Locale.getText(
        "PERMISSION_INVENTORY_FEE_MANAGER"
      ) as string
    }
  });

  /*
   * FR-035's distinction, and the one most likely to be built wrongly: "no permissions" and "no
   * *granted* permissions" are different states. Empty means **zero rows returned**. A user granted
   * none of the rows returned sees the full not-granted list, which is different and more useful
   * information — so this must be computed from row count, never from a granted count.
   *
   * `administrator` is excluded from "empty" because `TabState` substitutes the empty message for
   * `children` *entirely*. An administrator with zero rows is a real combination, and on row count
   * alone it would render the empty message and drop the administrator entry below — contradicting
   * FR-034's guarantee that the entry renders for both states. The empty message is only correct
   * when there is genuinely nothing to show.
   */
  const isEmpty = permissions.length === 0 && !data?.administrator;

  return (
    <TabState
      emptyMessage={Locale.getText("EMPTY_PERMS") as string}
      isEmpty={isEmpty}
      isError={isError}
      isLoading={isPending}
      loadingActionName={`Loading ${area.label} feature access`}
      testId={"feature-access"}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        {/*
         * T068. A heading-level anchor, visually hidden.
         *
         * MUI's `Accordion` wraps each group summary in its own `<h3>` (Phase 4 note 3), and this tab
         * had no `h2` of its own — so the document went `h1` → `h3`, skipping a level. A screen-reader
         * user navigating by heading sees a broken outline, and it is the one WCAG 1.3.1 problem on
         * this page that no amount of per-component correctness fixes, because it only exists in the
         * *combination* of the page's `h1` and MUI's `h3`.
         *
         * Hidden rather than visible because the tab panel is already labelled by its tab, so a
         * visible "Feature Access" heading directly under the "Feature Access" tab would be redundant
         * to a sighted user while adding nothing for anyone. It reuses `FEATURE_ACCESS` — the same
         * copy as the tab — so it introduces no new label key and cannot drift from the tab name.
         *
         * The User Information tab needs no equivalent: it already emits two real `h2` sections.
         * Account Access and Login History emit no headings at all, so they skip nothing.
         */}
        <Typography sx={SCREEN_READER_ONLY} tag={"h2"} variant={"h4"}>
          {Locale.getText("FEATURE_ACCESS") as string}
        </Typography>

        {/*
         * **Rendered only when the tab is read-only**, which is precisely when `authority` is
         * absent — the same condition that keeps every row non-grantable, so the explanation and the
         * behavior cannot disagree.
         *
         * For **My Info** that is always (it passes no `authority`), so its shipped behavior is
         * unchanged — the notice was previously unconditional and is now conditional on something
         * that is always true there. For **User Manager** it is exactly when the operator has
         * selected **themselves**, which is FR-009/FR-055: "a user cannot edit their own access
         * permissions".
         *
         * The condition is deliberately NOT a separate `isSelf` prop. User Manager decides it once
         * at the shell by withholding `authority`, and SC-005 wants the smallest possible surface
         * for "zero paths by which a user can change their own access permissions".
         */}
        {!authority && <ReadOnlyNotice />}

        {/*
         * FR-047. A **standing property of the tab**, not a success toast: shown *before* saving,
         * whenever the tab renders for an editable subject, because it describes a consequence of
         * the change rather than a result of one. It sits **alongside** the read-only notice rather
         * than instead of it — the two are mutually exclusive in practice, since one renders when
         * `authority` is absent and this one when it is present.
         */}
        {authority && (
          <Alert
            data-testid={"permission-change-signs-out-notice"}
            role={"status"}
            type={"caution"}
          >
            {Locale.getText("UM_PERMISSION_CHANGE_SIGNS_OUT") as string}
          </Alert>
        )}

        {/*
         * FR-034. Rendered from `data.administrator` — the sibling field on this tab's own response,
         * beside `permissions` rather than inside it. It is deliberately **outside** the grouped
         * list: no CC9 feature code exists for it, `groupId` is never `"global"`, and a live search
         * for a row named /admin/i returns nothing. Keeping it out of `groups` is what stops a
         * reader mistaking it for a row now that it arrives on the same response as the rows.
         *
         * The entry renders for `false` as well as `true`, so a non-administrator can see the state
         * rather than infer it from an absence.
         *
         * `data` is guaranteed here: `TabState` renders children only after a successful, non-empty
         * query. The guard is what narrows the type without a cast — and it also means a *failed*
         * read shows no administrator entry at all, rather than inventing "not an administrator"
         * from an absent response.
         */}
        {data ? (
          <Box sx={{ position: "relative" }}>
            {/* Only a non-administrator operator is barred; no authority means read-only. */}
            <CheckBox
              checked={isAdministratorChecked}
              data-testid={"administrator-entry"}
              disabled={!authority || isAdministratorNotGrantable}
              id={"permission-administrator"}
              label={Locale.getText("PERM_ADMINISTRATOR") as string}
              name={"administrator"}
              value={"administrator"}
              onChange={handleAdministratorToggle}
            />
            {isAdministratorNotGrantable && (
              <Box
                data-testid={"administrator-entry-reason"}
                sx={SCREEN_READER_ONLY}
              >
                {Locale.getText("UM_PERMISSION_NOT_GRANTABLE") as string}
              </Box>
            )}
          </Box>
        ) : null}

        {/*
         * Each group owns its own expansion state — see `PermissionGroup` for why Interstate's
         * `AccordionGroup` is deliberately not used here. `map` over an array, never a spread of a
         * Map, so no TS2802 (research R-011).
         */}
        <Box data-testid={"permission-groups"}>
          {groups.map(group => (
            <PermissionGroup
              authority={authority}
              group={group}
              isAdministratorChecked={isAdministratorChecked}
              key={group.key}
            />
          ))}
        </Box>
      </Box>
    </TabState>
  );
};
